import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useFlags } from '@databases/flags';
import { PerformanceTimingManager } from '@databases/frontend-metrics/performance-timing-manager';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { doAddEntry } from '@databases/redux/table-layout/actions';
import { getService } from '@databases/service-container';
import { type DatabaseEntry } from '@databases/types/database';
import { matchesFilter } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

/** Returns a function to handle adding a new entry to the database. */
const useAddEntry = (contentId: string, onAdd?: (entry: DatabaseEntry) => void) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();
  const selectionsActions = getService('SelectionsActions');
  const client = useAnalytics();
  const { formatMessage } = useIntl();
  const { showPanel } = usePanel();
  const { addFlag, clearFlags, showFlag } = useFlags();
  const {
    view: { settings: viewSettings },
  } = useView();

  return useCallback(() => {
    const reliability = ReliabilityMetrics.getInstance();
    try {
      const clickTime = performance.now();
      selectionsActions.deselectAll();
      client.sendAddEntryButtonClickedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'tableLayout',
      });
      const entry = dispatch(doAddEntry(contentId, viewSettings));
      PerformanceTimingManager.startMetricTiming({
        metricName: MetricName.DATABASE_ADD_ENTRY_TIME,
        id: entry.id,
        startTime: clickTime,
      });
      onAdd?.(entry);
      reliability.success(MetricName.DATABASE_ADD_ENTRY);

      // Check if the new entry will be filtered out of the current view
      if (viewSettings.filter) {
        const state = store.getState();
        const database = selectDatabaseByContentIdRequired(state, {
          contentId,
        });
        const isEntryVisible = matchesFilter(
          { database, entry },
          viewSettings.filter,
          state
        );

        if (!isEntryVisible) {
          const flagTitle = formatMessage(
            messages['table-layout.entry-hidden-by-filters.title']
          );
          const flagDescription = formatMessage(
            messages['table-layout.entry-hidden-by-filters.description']
          );
          const showEntryAction = formatMessage(
            messages['table-layout.entry-hidden-by-filters.show-entry']
          );

          const baseFlagData = {
            title: flagTitle,
            description: flagDescription,
            actions: [
              {
                content: showEntryAction,
                onClick: () => {
                  showPanel('EntryViewPanel', {
                    entryId: entry.id,
                    contentId,
                  });
                  clearFlags();
                },
              },
            ],
          };

          if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
            showFlag({
              ...baseFlagData,
              id: `add-entry-hidden-${entry.id}`,
              type: 'success-circle',
            });
          } else {
            addFlag({
              ...baseFlagData,
            });
          }
        }
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      reliability.failure(MetricName.DATABASE_ADD_ENTRY, { errorMessage });
      throw e;
    }
  }, [
    addFlag,
    clearFlags,
    client,
    contentId,
    dispatch,
    formatMessage,
    onAdd,
    selectionsActions,
    showFlag,
    showPanel,
    store,
    viewSettings,
  ]);
};

export default useAddEntry;
